import { useLocation } from "react-router";

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizedPathName = (string) => {
  return string.charAt(1).toUpperCase() + string.slice(2);
};

export const makeSearchParams = (query) => {
  return new URLSearchParams(query).toString();
};

export const ellipsify = (str, charLen = 10, spreader = "...") => {
  if (!str) {
    return "";
  }

  if (str.length > charLen) {
    return str.substring(0, charLen) + spreader;
  }

  return str;
};

export const ellipsifyAtStart = (str = "", charLen = 10, spreader = "...") => {
  if (str.length > charLen) {
    return spreader + str.substring(str.length - charLen, str.length);
  }
  return str;
};

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Decodes HTML entities in a string
 * For example: "Father&#39;s Day" becomes "Father's Day"
 *
 * @param {string} encodedString - String with HTML entities
 * @returns {string} - Decoded string with HTML entities replaced
 */
export const decodeHtmlEntities = (encodedString) => {
  if (!encodedString || typeof encodedString !== "string") return encodedString;

  // Create a temporary DOM element to decode the string
  const textArea = document.createElement("textarea");
  textArea.innerHTML = encodedString;
  // The innerHTML of the textarea now contains the text with entities decoded
  const decodedString = textArea.value;

  return decodedString;
};

/**
 * Parses a product title string by:
 * 1. Decoding HTML entities (e.g., &#39; becomes ')
 * 2. Cleaning up extra whitespace
 * 3. Optionally splitting into segments (separated by commas)
 *
 * @param {string} productTitle - The product title string to parse
 * @param {Object} options - Parsing options
 * @param {boolean} options.splitSegments - Whether to split the title by commas into segments
 * @param {boolean} options.trim - Whether to trim whitespace from the title/segments (default: true)
 * @returns {string|string[]} - Parsed string or array of segments if splitSegments is true
 */
export const parseProductTitle = (productTitle, options = {}) => {
  if (!productTitle || typeof productTitle !== "string") return productTitle;

  // Set default options
  const { splitSegments = false, trim = true } = options;

  // First decode HTML entities
  let parsedTitle = decodeHtmlEntities(productTitle);

  // Clean up whitespace
  if (trim) {
    parsedTitle = parsedTitle.trim();
  }

  // Split into segments if requested
  if (splitSegments) {
    let segments = parsedTitle.split(",");

    // Clean up segments if trimming is enabled
    if (trim) {
      segments = segments.map((segment) => segment.trim());
      // Remove any empty segments
      segments = segments.filter((segment) => segment.length > 0);
    }

    return segments;
  }

  return parsedTitle;
};
